import type { StoryObj } from '@storybook/html'
import type { CobaltInputText as CobaltInputTextProps } from './cobalt-input-text'

export default {
  title: 'Components/Form/cobalt-input-text',
  argTypes: {
    label: {
      type: { name: 'string', required: true },
      description: 'This is the text which accompany the input. It is **required** for accessibility concerns.'
    },
    value: {
      type: { name: 'string' },
      description: 'The value added inside the input. It can be used to add default value.'
    },
    placeholder: {
      type: { name: 'string', required: true },
      description: 'Placeholder inside the input.'
    },
    format: {
      type: { name: 'string', required: true },
      options: ['inside', 'outside', 'aside', 'hidden'],
      control: { type: 'select' },
      description: 'Position the label in different place.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'inside' }
      }
    },
    disabled: {
      control: 'boolean',
      options: [true, false],
      description: 'A boolean indicating the disable state of the input field. The `aria-disabled` attribute relies on this property.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    requirement: {
      type: { name: 'string', required: true },
      options: ['mandatory', 'optional', 'none'],
      control: { type: 'select' },
      description: 'Mention displayed to indicate if the field is mandatory or optional. If none is selected `custom-requirement` slot is available.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'mandatory' }
      }
    },
    cobaltInputTextValueChange: {
      description: 'Custom event emitted when input value change.',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltInputTextValueChange={handleChange}\nVue:@cobaltInputTextValueChange="handleChange"\nAngular:(cobaltInputTextValueChange)="handleChange()"\nVanillaJs:.addEventListener("cobaltInputTextValueChange", (event) => {//handle change});'
        }
      },

      action: 'cobaltInputTextValueChange'
    },
    cobaltInputTextTyping: {
      description: 'Custom event emitted when typing inside the input.',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltInputTextTyping={handleChange}\nVue:@cobaltInputTextTyping="handleChange"\nAngular:(cobaltInputTextTyping)="handleChange()"\nVanillaJs:.addEventListener("cobaltInputTextTyping", (event) => {//handle change});'
        }
      },

      action: 'cobaltInputTextTyping'
    }
  }
}

const Template = (args: CobaltInputTextProps) => {
  return `
<cobalt-input-text label="${args.label}" placeholder="${args.placeholder}" requirement="${args.requirement}" format="${args.format}" disabled="${args.disabled}"></cobalt-input-text>
</div>
`
}

export const Default: StoryObj = Template.bind({})

Default.args = {
  label: 'Input Label',
  value: '',
  placeholder: 'Placeholder',
  format: 'inside',
  requirement: 'mandatory',
  disabled: false
}

const TemplateDescription = (args: CobaltInputTextProps) => {
  return `
<cobalt-input-text label="${args.label}" placeholder="${args.placeholder}" format="outside" description="${args.description}"></cobalt-input-text>
`
}

export const WithDescription: StoryObj = TemplateDescription.bind({})

WithDescription.args = {
  label: 'Input Label',
  placeholder: 'Placeholder',
  description: 'A little description which helps understand what the input can do.'
}

const TemplateCustomRequirement = (args: CobaltInputTextProps) => {
  return `
<style>
  .custom-requirement {
    padding-left: var(--number-spacing-4xsmall);
  }
</style>
<cobalt-input-text label="${args.label}" placeholder="${args.placeholder}" requirement="${args.requirement}">
  <span slot="custom-requirement" class="custom-requirement">- Requis</span>
</cobalt-input-text>
`
}

export const WithCustomRequirement: StoryObj = TemplateCustomRequirement.bind({})

WithCustomRequirement.args = {
  label: 'Input Label',
  placeholder: 'Placeholder',
  requirement: 'none'
}

const TemplateWithErrorMessage = (args: CobaltInputTextProps) => {
  return `
<script>
  document.querySelector('#toggle-button').addEventListener('cobaltButtonClick', (e) => {
    if (document.querySelector('#input-text-in-error').hasAttribute('error-message')) {
      document.querySelector('#input-text-in-error').removeAttribute('error-message')
    } else {
      document.querySelector('#input-text-in-error').setAttribute('error-message', 'This field is required.')
    }
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <cobalt-button id="toggle-button" label="Toggle Error State"></cobalt-button>
  <cobalt-input-text id="input-text-in-error" label="${args.label}" placeholder="${args.placeholder}" ></cobalt-input-text>
</div>
`
}

export const WithErrorMessage: StoryObj = TemplateWithErrorMessage.bind({})

WithErrorMessage.args = {
  label: 'Input Label',
  placeholder: 'Placeholder'
}

const selectOptions = [
  { value: 'Value1' },
  { value: 'Value2' },
  { value: 'Value3' },
  { value: 'Value4', isDisabled: true },
  { value: 'Value5' },
  { value: 'Value6' },
  { value: 'Value7' },
  { value: 'Value8' },
  { value: 'Value9' },
  { value: 'Value10' }
]

const TemplateBeforeAfter = (args: CobaltInputTextProps) => {
  return `
<cobalt-input-text label="${args.label}" placeholder="${args.placeholder}">
  <cobalt-icon-button slot="before" icon-name="star-solid" label="action"></cobalt-icon-button>
  <cobalt-select slot="after" options='${JSON.stringify(selectOptions)}' requirement="none" label="after select" format="hidden"></cobalt-select>
</cobalt-input-text>
`
}

export const WithBeforeAndAfter: StoryObj = TemplateBeforeAfter.bind({})

WithBeforeAndAfter.args = {
  label: 'Input Label',
  placeholder: 'Placeholder'
}

const TemplateOnChangeEvent = (args: CobaltInputTextProps) => {
  return `
<script>
  document.querySelector('#input-text-on-change').addEventListener('cobaltInputTextValueChange', (e) => {
    document.querySelector('#input-change-value').textContent = e.detail
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <p class="typography-body-medium-regular">Value is: <span id="input-change-value">not defined yet</span></p>
  <cobalt-input-text id="input-text-on-change" label="${args.label}" placeholder="${args.placeholder}"></cobalt-input-text>
</div>
`
}

export const OnChangeEvent: StoryObj = TemplateOnChangeEvent.bind({})

OnChangeEvent.args = {
  label: 'Input Label',
  placeholder: 'Placeholder'
}

const TemplateOnTyping = (args: CobaltInputTextProps) => {
  return `
<script>
  document.querySelector('#input-text-on-typing').addEventListener('cobaltInputTextTyping', (e) => {
    document.querySelector('#input-typing-value').textContent = e.detail
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <p class="typography-body-medium-regular">Value is: <span id="input-typing-value">not defined yet</span></p>
  <cobalt-input-text id="input-text-on-typing" label="${args.label}" placeholder="${args.placeholder}"></cobalt-input-text>
</div>
`
}

export const OnTypingEvent: StoryObj = TemplateOnTyping.bind({})

OnTypingEvent.args = {
  label: 'Input Label',
  placeholder: 'Placeholder'
}
